import {onMounted, reactive, ref, watch} from 'vue';
import Debounce from './Debounce.js';

export function useDataTable(fetchRecords, orderColumnDefault = 'created_at', pageLengthDefault = 10, directionAscDefault = true, filterDefault = {}, localStorageSubKey = null, datatableRef = null) {
    const localStorageKey = 'TABLE-SETTINGS';
    const localStorageValue = localStorage.getItem(localStorageKey);
    const orderAsc = ref(directionAscDefault);
    const orderColumn = ref(orderColumnDefault);
    const pageLength = ref(pageLengthDefault);
    const search = ref('');
    const filter = ref(filterDefault);
    const page = ref(1);
    const totalRecords = ref(0);
    const isLoading = ref(false);
    const rows = ref([]);
    // Řazení pro :sort-options="{enabled: true, initialSortBy: datatable.initialSortBy.value}" – šipka v hlavičce
    // odpovídá řazení dat. Nastavuje se hned (ne až po připojení tabulky), aby ho tabulka převzala při mountu.
    const initialSortBy = ref({field: orderColumnDefault, type: directionAscDefault ? 'asc' : 'desc'});
    // Při obnovení stránkování vyvolá tabulka sama událost per-page-change – nesmí se brát jako změna od uživatele
    // (onPerPageChange by vrátil stránku na 1 a uložil ji přes obnovenou).
    let restoringPagination = false;

    // if we have table settings in LS, we set them
    if (localStorageValue && localStorageSubKey) {
        const localStorageSubValue = JSON.parse(localStorageValue)[localStorageSubKey];
        if (localStorageSubValue) {
            orderColumn.value = localStorageSubValue.orderColumn ?? orderColumnDefault;
            orderAsc.value = localStorageSubValue.orderAsc ?? directionAscDefault;
            pageLength.value = localStorageSubValue.pageLength ?? pageLengthDefault;
            search.value = localStorageSubValue.search ?? '';
            // výchozí filtr doplní klíče, které v uloženém chybí – opraví i dříve uložené nastavení,
            // ze kterého filtr sloupců vyřadil pevnou podmínku (např. resolved v Servisním centru)
            filter.value = {...filterDefault, ...(localStorageSubValue.filter ?? {})};
            page.value = localStorageSubValue.page ?? 1;
            initialSortBy.value = {field: orderColumn.value, type: orderAsc.value ? 'asc' : 'desc'};

            // stránkování a filtry v samotné tabulce (vue-good-table-next) až po jejím připojení
            setTimeout(() => {
                const datatable = datatableRef ? datatableRef.value : null;
                if (!datatable) {
                    return;
                }
                restoringPagination = true;
                try {
                    // nejdřív počet řádků (vyšle per-page-change), teprve potom stránka – jinak by ji vrátil na 1
                    if (datatable.perPageChanged) {
                        datatable.perPageChanged({currentPerPage: pageLength.value});
                    } else {
                        datatable.currentPerPage = pageLength.value;
                    }
                    // výběr počtu řádků v patičce: tabulka ho sama přenese jen při nastavené position stránkování
                    for (const pagination of [datatable.$refs?.paginationBottom, datatable.$refs?.paginationTop]) {
                        if (pagination) {
                            pagination.currentPerPage = pageLength.value;
                        }
                    }
                    datatable.changePage(page.value);
                } finally {
                    restoringPagination = false;
                }

                datatable.columnFilters = filter.value;
                datatable.columns.forEach(column => {
                    // sloupec bez filtru (filterOptions) se přeskočí
                    if (filter.value?.[column.field] && column.filterOptions) {
                        column.filterOptions.filterValue = filter.value[column.field];
                    }
                });
            });
        }
    }

    const body = reactive({
        page: page,
        search: search,
        pageLength: pageLength,
        orderColumn: orderColumn,
        orderAsc: orderAsc,
        filter: filter
    });

    function onPageChange(params) {
        page.value = params.currentPage;
    }

    function onPerPageChange(params) {
        if (restoringPagination) {
            return;
        }
        page.value = 1;
        pageLength.value = params.currentPerPage;
    }

    function onSortChange(params) {
        if (params.length > 0) {
            orderBy(params[0].field, params[0].type);
        }
    }

    function onColumnFilter(params) {
        page.value = 1;
        // tabulka posílá jen hodnoty sloupců – bez sloučení s výchozím filtrem by se ztratila pevná
        // podmínka, kterou sloupce nemají (např. resolved u záložek Servisního centra)
        filter.value = {...filterDefault, ...params.columnFilters};
    }

    function getReactiveValues() {
        return {
            search: search.value,
            pageLength: pageLength.value,
            page: page.value,
            orderColumn: orderColumn.value,
            orderAsc: orderAsc.value,
            filter: filter.value,
        };
    }

    function fetchWithValues() {
        isLoading.value = true;
        fetchRecords(getReactiveValues());
        saveToLocalStorage();
    }

    function saveToLocalStorage() {
        if (localStorageSubKey) {
            localStorage.setItem(localStorageKey, JSON.stringify({
                ...JSON.parse(localStorage.getItem(localStorageKey)),
                [localStorageSubKey]: getReactiveValues()
            }));
        }
    }

    onMounted(() => {
        fetchWithValues();
    });

    function orderBy(column, type) {
        // tabulka při připojení ohlásí výchozí řazení (initialSortBy) – stejné řazení znovu nenačítáme
        if (orderColumn.value === column && orderAsc.value === (type === 'asc')) {
            return;
        }
        orderColumn.value = column;
        orderAsc.value = type === 'asc';
        fetchWithValues();
    }

    watch(search, () => {
        isLoading.value = true;
        rows.value = [];
        Debounce(fetchWithValues, 500)();
    });
    watch(filter, () => {
        isLoading.value = true;
        rows.value = [];
        Debounce(fetchWithValues, 500)();
    }, {deep: true});
    watch(pageLength, fetchWithValues);
    watch(page, fetchWithValues);

    return {
        orderAsc,
        orderColumn,
        pageLength,
        search,
        filter,
        isLoading,
        rows,
        totalRecords,
        onColumnFilter,
        onPageChange,
        onPerPageChange,
        onSortChange,
        fetchWithValues,
        body,
        initialSortBy
    };
}
